import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Duration in ms for tile reactions to display
const REACTION_DURATION = 3000;

export default function TileReaction({ reaction, onComplete }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (reaction) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, REACTION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [reaction, onComplete]);

  if (!reaction) return null;

  const isBoosted = reaction.boosted;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Badge on tile - appears at bottom right of participant video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -10 }}
            transition={{ duration: 0.3 }}
            className={`absolute bottom-2 right-2 backdrop-blur-sm rounded-full p-1.5 shadow-lg z-10 ${
              isBoosted
                ? 'bg-purple-500/40 ring-2 ring-purple-400/50'
                : 'bg-black/60'
            }`}
          >
            <span className="text-xl">{reaction.emoji}</span>
          </motion.div>

          {/* Floating animation for boosted reactions only */}
          {isBoosted && (
            <FloatingAnimation emoji={reaction.emoji} />
          )}
        </>
      )}
    </AnimatePresence>
  );
}

// Floating animation component for boosted reactions on the tile
function FloatingAnimation({ emoji }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Create 3 floating particles
    const newParticles = Array.from({ length: 3 }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      delay: i * 0.15,
      x: (Math.random() - 0.5) * 60,
    }));
    setParticles(newParticles);

    // Clean up after animation
    const timer = setTimeout(() => {
      setParticles([]);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          initial={{
            opacity: 1,
            scale: 0.5,
            y: '80%',
            x: `calc(50% + ${particle.x}px)`,
          }}
          animate={{
            opacity: 0,
            scale: 1.5,
            y: '-20%',
          }}
          transition={{
            duration: 1.2,
            delay: particle.delay,
            ease: 'easeOut',
          }}
          className="absolute text-2xl"
          style={{
            filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))',
          }}
        >
          {emoji}
        </motion.div>
      ))}
    </div>
  );
}
